/**
 * The ref picker popover (SPEC §6.2): anchored under the trigger, keyboard-driven from the
 * textarea (↑/↓, ⏎/Tab, Esc in keys.ts), clickable without stealing focus.
 */
import { Building2, CalendarDays, FileText, Hash, Plus, Text, User } from 'lucide-react'
import { createPortal } from 'react-dom'
import { companyOf, isCompany, isPerson, type Graph, type Node } from '@solar/core'
import { caretCoords } from '../client/caret.ts'
import { blockLine } from '../client/picker.ts'
import { useStore, useUi } from './hooks.ts'

function KindIcon({ g, n }: { g: Graph; n: Node }) {
  const props = { size: 14, 'aria-hidden': true as const }
  if (n.kind === 'tag') return <Hash {...props} />
  if (n.kind === 'journal') return <CalendarDays {...props} />
  if (n.kind === 'block') return <Text {...props} />
  if (isPerson(g, n)) return <User {...props} />
  if (isCompany(g, n)) return <Building2 {...props} />
  return <FileText {...props} />
}

export function Picker() {
  const store = useStore()
  useUi()
  const p = store.picker
  const ta = store.textarea
  if (!p || !ta || ta.dataset.id !== p.blockId || !ta.isConnected) return null
  const c = caretCoords(ta, p.from)
  const below = c.top + c.height + 300 < window.innerHeight
  const style = below ? { left: c.left, top: c.top + c.height + 4 } : { left: c.left, top: c.top - 4, transform: 'translateY(-100%)' }
  return createPortal(
    <div className="picker" style={style} role="listbox" aria-label="Suggestions" onMouseDown={(e) => e.preventDefault()}>
      {p.items.length === 0 && <div className="picker-empty">{p.kind === 'block' ? 'Type to search blocks' : 'No matches'}</div>}
      {p.items.map((it, i) => (
        <div
          key={it.type === 'create' ? 'create' : it.node.id}
          className={i === p.active ? 'item active' : 'item'}
          role="option"
          aria-selected={i === p.active}
          onMouseEnter={() => store.setPickerActive(i)}
          onMouseDown={(e) => {
            e.preventDefault()
            store.pickItem(i)
          }}
        >
          {it.type === 'create' ? (
            <>
              <Plus size={14} aria-hidden />
              <span className="name">
                {p.kind === 'mention' ? 'Create person' : 'Create'} “<b>{it.title}</b>”
              </span>
            </>
          ) : it.type === 'block' ? (
            <>
              <Text size={14} aria-hidden />
              <span className="name">{blockLine(it.node)}</span>
            </>
          ) : (
            <>
              <KindIcon g={store.g} n={it.node} />
              <span className="name">{it.node.title}</span>
              {isPerson(store.g, it.node) && companyOf(store.g, it.node) && <span className="hint">{companyOf(store.g, it.node)!.title}</span>}
            </>
          )}
        </div>
      ))}
    </div>,
    document.body,
  )
}
